/**
 * @file 导入弹窗-下载模板
 */

import { Button, Modal, Toast } from '@seiue/ui'
import { formatFileSize } from '@seiue/util'
import { Upload } from 'antd'
import { GrowpIcon } from 'packages/components/GrowpIcon'
import { THEME_COLOR } from 'packages/theme'
import { AnyType } from 'packages/types'
import { useState } from 'react'
import styled from 'styled-components'

// import { env } from 'packages/utils'
import { ActionType } from './types'

import { ImportProps } from './index'

/**
 * 导入活动用户
 *
 * @param param0 - 参数
 * @param param0.activityId - 活动ID
 * @param param0.hide - 关闭方法
 * @returns 组件
 */
export const ImportModal: React.FC<
  ImportProps & {
    onCancel: () => void
  }
> = ({
  title,
  action = ActionType.Create,
  onClickDownload,
  onCancel,
  uploadRemote,
  beforeUpload,
  maxUploadFileSize = 10 * 1024 * 1024,
  tipTitle,
  tip,
  exportBtnText,
}) => {
  const isUpdate = action === ActionType.Update
  const { Dragger } = Upload

  const [file, setFile] = useState<AnyType>(undefined)
  const [uploading, setUploading] = useState(false)

  const upload = async () => {
    if (file) {
      setUploading(true)

      if (beforeUpload) {
        try {
          const result = await beforeUpload(file)
          if (!result) {
            setUploading(false)
            return
          }
        } catch (e) {
          setUploading(false)
          throw e
        }
      }

      try {
        const data = await uploadRemote(file)
        if (data) {
          onCancel()
        } else {
          setFile(undefined)
        }
      } finally {
        setUploading(false)
      }
    }
  }

  return (
    <>
      <Box>
        <DownloadBox>
          <Title>
            {tipTitle
              ? `1. ${tipTitle}`
              : `1. 下载${isUpdate ? '更新' : '导入'}模板`}
          </Title>
          <Tip>{tip || '根据提示信息完善表格内容'}</Tip>
          <DownBtn
            type="special"
            onClick={() => {
              onClickDownload()
            }}
          >
            <DownText>
              <GrowpIcon name="Xiazai" />
              <span>{exportBtnText || '下载模板表格'}</span>
            </DownText>
          </DownBtn>
        </DownloadBox>
        <ImportBox>
          <Title>2. 上传完善后的模板</Title>
          <Dragger
            height={182}
            beforeUpload={(file) => {
              if (file && file.size && file.size > maxUploadFileSize) {
                Toast.error(
                  `导入的文件最大为 ${formatFileSize(maxUploadFileSize)}`
                )
              } else {
                setFile(file)
              }
              return false
            }}
            onChange={({ file }) => {
              if (file && file.size && file.size > maxUploadFileSize) {
                Toast.error(
                  `导入的文件最大为 ${formatFileSize(maxUploadFileSize)}`
                )
              } else {
                setFile(file)
              }
            }}
            maxCount={1}
          >
            <ImportBtn>
              <GrowpIcon
                name="Shangchuanwenjian"
                size={48}
              />
            </ImportBtn>
            <Title>点击或拖拽文件到此处上传</Title>
            <Tip>
              下载模板并完善信息后，可将文件拖拽到此处进行上传。支持格式：.xls、.xIsx
            </Tip>
          </Dragger>
        </ImportBox>
      </Box>
      <Modal.Footer
        onCancel={onCancel}
        onOk={upload}
        okText={
          uploading
            ? `正在${isUpdate ? '更新' : '导入'}...`
            : isUpdate
            ? '更新'
            : '导入'
        }
        cancelText={'取消'}
        okButtonProps={{
          disabled: !file,
          'data-test-id': '上传文件_开始上传',
          loading: uploading,
        }}
      />
    </>
  )
}

const Box = styled.div`
  margin: 12px 20px 20px 20px;
`

const DownloadBox = styled.div`
  height: 128px;
  margin-bottom: 12px;
  border-radius: 8px;
  background-color: ${(props) => props.theme.background._3};
  padding-top: 20px;
  padding-left: 20px;
`

const ImportBox = styled.div`
  height: 260px;
  border-radius: 8px;
  background-color: ${(props) => props.theme.background._3};
  padding: 20px;
  &&& {
    .ant-upload.ant-upload-drag:not(.ant-upload-disabled):hover {
      border-color: ${(props) => props.theme.brand._1};
    }
  }
`

const Title = styled.div`
  height: 22px;
  font-size: 14px;
  margin-bottom: 4px;
`

const Tip = styled.div`
  color: ${(props) => props.theme.text._3};
`

const DownBtn = styled(Button)`
  margin-top: 8px;
`

const DownText = styled.div`
  display: flex;
  gap: 8px;
  color: ${(props) => props.theme.brand._1};
  &&& {
    path {
      fill: ${(props) => props.theme.brand._1};
    }
  }
`

const ImportBtn = styled.div`
  display: flex;
  justify-content: center;
`

const FooterBox = styled.div`
  border-top: 1px solid ${(props) => props.theme.border.greyNormal};
  height: 56px;
  display: flex;
  justify-content: right;
  padding: 12px 20px;
`

const BtnBox = styled.div``
